import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useCustomQuery } from "@/queries/queryUtils";
import { getShifts } from "@/queries/api/shiftApi";
import { useSendOfferDaysMutation } from "@/queries/hooks/businessShiftHooks";
import { ShiftCardItem } from "@/components/shift-card";
import { OfferDaysPicker } from "@/components/shift-detail/offer-days-picker";
import { collapseSeries, shiftToCard } from "@/utils/shift-card";
import { notify } from "@/utils";
import { useState } from "react";
import BackButton from "@/components/back-button";
import { makeProfileQuery } from "./-queries";

export const Route = createFileRoute(
  "/app/profile/worker/$workerId/send-offer",
)({
  component: SendOfferPage,
});

function SendOfferPage() {
  const { t } = useTranslation("profile");
  const { workerId } = Route.useParams();
  const { data: profileData } = useSuspenseQuery(makeProfileQuery(workerId));
  const profile = profileData.profile;
  const navigate = useNavigate();

  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);
  const { data, isLoading, error } = useCustomQuery(
    ["business-shifts-for-offer"],
    () => getShifts({ page_size: 20 }),
    { staleTime: 60_000 },
  );
  // A multi-day series is listed once; picking it opens a day picker below.
  const shifts = collapseSeries(data?.shifts || []);
  const [dayIds, setDayIds] = useState<Array<number>>([]);
  const offerMutation = useSendOfferDaysMutation();

  const handleSendOffer = () => {
    if (!selectedShiftId || dayIds.length === 0) return;
    offerMutation.mutate(
      { userId: profile.user_id, shiftIds: dayIds },
      {
        onSuccess: ({ sent, failed }) => {
          if (sent > 0) {
            notify.success(
              dayIds.length > 1
                ? t("v2.shift.series.offersSent", { ns: "dashboard", count: sent })
                : t("sendOffer.sent"),
            );
          }
          if (failed > 0) {
            notify.error(
              dayIds.length > 1
                ? t("v2.shift.series.offersFailed", { ns: "dashboard", count: failed })
                : t("sendOffer.failed"),
            );
          }
          if (sent > 0) {
            setTimeout(
              () => navigate({ to: `/app/profile/worker/${workerId}` }),
              1200,
            );
          }
        },
        onError: () => {
          notify.error(t("sendOffer.failed"));
        },
      },
    );
  };

  return (
    <div className="w-full min-h-[calc(100vh-64px)] bg-fog flex flex-col gap-6 py-8 px-2 md:px-8">
      <div className="max-w-[820px] mx-auto">
        <div className="mb-6 flex items-center gap-4">
          <BackButton
            onClick={() => navigate({ to: `/app/profile/worker/${workerId}` })}
          />
          <h1 className="text-2xl font-bold">
            {t("sendOffer.title", { name: profile.full_name })}
          </h1>
        </div>
        <div className="flex flex-col items-center mb-4">
          <Avatar className="w-16 h-16">
            <AvatarImage src={profile.avatar_url} alt={profile.full_name} />
            <AvatarFallback>
              {profile.full_name ? profile.full_name[0] : "W"}
            </AvatarFallback>
          </Avatar>
          <div className="text-lg font-semibold mt-2">{profile.full_name}</div>
        </div>
        <div className="mb-4">
          <div className="font-semibold mb-2">{t("sendOffer.selectShift")}</div>
          {isLoading && <div>{t("sendOffer.loadingShifts")}</div>}
          {error ? (
            <div className="text-red-500">{t("sendOffer.loadFailed")}</div>
          ) : null}
          {!isLoading && shifts.length === 0 && (
            <div>{t("sendOffer.noShifts")}</div>
          )}
          <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(300px,1fr))] max-h-96 overflow-y-auto max-w-[820px] mx-auto">
            {shifts.map((shift: any) => {
              const card = shiftToCard(shift, {
                businessName: shift.business?.name,
              });
              if (shift.seriesCount > 1) {
                card.dateLabel = `${card.dateLabel} · ${t("v2.shift.series.daysCount", { ns: "dashboard", count: shift.seriesCount })}`;
              }
              return (
                <div
                  key={shift.id}
                  className={`border rounded-lg p-2 cursor-pointer ${selectedShiftId === shift.id ? "border-ember bg-ember/10" : "border-fog"}`}
                  onClick={() => setSelectedShiftId(shift.id)}
                >
                  <ShiftCardItem s={card} hideUser={true} />
                </div>
              );
            })}
          </div>
        </div>
        {selectedShiftId ? (
          <OfferDaysPicker shiftId={Number(selectedShiftId)} onChange={setDayIds} />
        ) : null}
        <button
          className="max-w-[320px] w-full h-11 rounded-[25px] font-semibold text-white border border-ember bg-ember hover:bg-ember/90 transition mt-2 disabled:opacity-50"
          disabled={!selectedShiftId || dayIds.length === 0 || offerMutation.isPending}
          onClick={handleSendOffer}
        >
          {offerMutation.isPending
            ? t("sendOffer.sending")
            : t("sendOffer.send")}
        </button>
      </div>
    </div>
  );
}
